import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { DataStorageNotice } from "@/components/data-storage-notice";
import { Button } from "@/components/ui/button";
import { useLogout, useMe } from "@/lib/api";
import { PRIVACY_POLICY_PATH } from "@/lib/legal";
import { isStaffRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

interface PageShellProps {
  children: ReactNode;
  /** Widens the content column (e.g. for tables). */
  wide?: boolean;
  /** Header logo destination. Defaults to the public landing page. */
  logoTo?: string;
}

/**
 * Shared shell for the assessment, report, expert, and history pages: slim
 * branded header with auth-aware nav, centered content column, and the
 * standing diagnostic + data-storage disclaimer.
 */
export function PageShell({ children, wide = false, logoTo = "/" }: PageShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // Cheap logged-in probe (retry: false); anonymous visitors just get a 401.
  // Rendering never waits on it; the nav simply appears once resolved.
  const { data: me, isLoading } = useMe();
  const logoutMutation = useLogout();

  // "Start assessment" is the app's core action; it shows in both auth states.
  // Hide it where it would only duplicate something already on the page: on
  // /assess it would be a self-link, and the landing hero already carries the
  // same CTA. Everywhere else (report, history, /privacy) the nav is the only
  // route back into the funnel, so it stays.
  const ctaIsRedundant = location.pathname === "/assess" || location.pathname === "/";

  const handleSignOut = async () => {
    await logoutMutation.mutateAsync();
    navigate("/");
  };

  return (
    // `data-print-*` are the hooks the @media print block in globals.css uses to
    // undo the screen shell (see the print section there).
    <div data-print-shell className="bg-background flex min-h-screen flex-col">
      <header className="border-border/80 bg-background/90 sticky top-0 z-10 border-b px-6 py-4 backdrop-blur print:hidden">
        <div
          className={cn(
            "mx-auto flex flex-wrap items-center justify-between gap-4",
            wide ? "max-w-5xl" : "max-w-3xl",
          )}
        >
          <Link to={logoTo} className="flex items-center gap-3">
            <img src="/brand/boi-logo-black.svg" alt="Board of Innovation" className="h-6 w-auto" />
            <span className="bg-border hidden h-6 w-px sm:block" />
            <span className="text-primary hidden text-base font-medium tracking-tight sm:block">
              AI Disruption Exposure Index
            </span>
          </Link>
          <nav className="flex shrink-0 items-center gap-1">
            {!ctaIsRedundant && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/assess">Start assessment</Link>
              </Button>
            )}
            {!isLoading && me && (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/history">History</Link>
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/profile">Profile</Link>
                </Button>
                {isStaffRole(me.role) && (
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/expert">Expert review</Link>
                  </Button>
                )}
                {me.role === "admin" && (
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/admin/users">Admin</Link>
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => void handleSignOut()}>
                  Sign out
                </Button>
              </>
            )}
            {!isLoading && !me && (
              <Link to="/login" className="text-muted hover:text-primary text-sm font-medium">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main
        data-print-content
        className={cn(
          "mx-auto w-full flex-1 px-6 pt-10 pb-16 sm:pt-16 sm:pb-24",
          wide ? "max-w-5xl" : "max-w-3xl",
        )}
      >
        {children}
      </main>
      <footer className="bg-primary px-6 pt-10 pb-8 print:hidden">
        <div
          className={cn(
            "mx-auto flex flex-wrap items-start justify-between gap-6",
            wide ? "max-w-5xl" : "max-w-3xl",
          )}
        >
          <div className="flex flex-col gap-3">
            {/* Leaves the app for boardofinnovation.com, so it's an <a>, not a
                react-router <Link>, and opens in a new tab rather than dropping
                someone out of a half-finished assessment. */}
            <a
              href="https://www.boardofinnovation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit"
            >
              <img
                src="/brand/boi-logo-off-white.svg"
                alt="Board of Innovation"
                className="h-5 w-auto"
              />
            </a>
            <Link
              to={PRIVACY_POLICY_PATH}
              className="text-primary-foreground/70 hover:text-primary-foreground text-xs"
            >
              Privacy policy
            </Link>
          </div>
          <p className="text-primary-foreground/70 max-w-[52ch] text-xs leading-relaxed">
            A diagnostic, not a full strategy. Results reflect the information provided and should
            be reviewed with a BOI expert before major decisions. <DataStorageNotice />
          </p>
        </div>
      </footer>
    </div>
  );
}
